import { useContext, useState, useEffect, useRef } from 'react';

import { Context } from 'components/Store';
import useDebounce from 'hooks/useDebounce';
import { genProxyResource } from 'utilities/api';
import { PROXY_SEARCH_MODEL } from 'utilities/helpers/searchModels';
import PAGINATION_SIZE from 'helpers/globalConstants';
import catchCancel from '../helpers/catchCancel';
import { parseServerError } from '../helpers/errorHelpers';

const createQuery = (
  searchField,
  searchTerm,
  cropSeason,
  productType,
  taskType,
  flag
) => {
  const queryParams = { filters: [] };
  if (searchTerm) {
    queryParams.filters.push({ [searchField]: searchTerm });
  }
  if (cropSeason && cropSeason !== 'none') {
    queryParams.filters.push({ 'cropSeason.cropSeasonLabel': cropSeason });
  }
  if (flag && flag !== 'none') {
    queryParams.filters.push({ flag });
  }
  if (taskType && taskType !== 'none') {
    queryParams.filters.push({ taskType });
  }
  if (productType && productType !== 'none') {
    queryParams.filters.push({ productType });
  }
  return queryParams;
};

const useLandingPageData = (
  apiResource,
  _sortFields,
  searchField,
  pageNo = 0
) => {
  const [view, setView] = useState('comfortable');
  const [sort, setSort] = useState('asc');
  const [sortByOverride, setSortByOverride] = useState(
    /** @type {string|null} */ (null)
  );
  const [isSearch, setIsSearch] = useState(false);
  const [searchText, setSearchText] = useState(null);
  const [cropSeason, setCropSeason] = useState(null);
  const [productType, setProductType] = useState(null);
  const [taskType, setTaskType] = useState(null);
  const [label, setLabel] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [loadedSearchTerm, setLoadedSearchTerm] = useState('');
  const [{ organization, loadingOrg }, dispatch] = useContext(Context);
  const [items, setItems] = useState([]);
  const [reload, setReload] = useState(false);
  const [totalItems, setTotalItems] = useState(0);

  const api = useRef(genProxyResource(apiResource, { urlVersion: '/v1/base' }));
  const requestId = useRef(0);
  const debouncedSearchTerm = useDebounce(searchText, 300);
  const isLoading = !loaded || (searchText || '') !== loadedSearchTerm;

  useEffect(() => {
    const currentRequestId = requestId.current + 1;
    requestId.current = currentRequestId;
    let cancelRequest;

    const loadItems = (sortDir, searchTerm) => {
      if (!api.current) {
        return;
      }
      const query = createQuery(
        searchField,
        searchTerm,
        cropSeason,
        productType,
        taskType,
        label
      );
      const params = {
        pageNo,
        size: PAGINATION_SIZE,
        sort: `${sortByOverride || 'name'},${sortDir}`,
        ...(searchTerm ? { name: searchTerm } : {})
      };
      const { promise, cancel } = api.current.find(
        query,
        PROXY_SEARCH_MODEL,
        params
      );
      cancelRequest = cancel;
      promise
        .then(({ data }) => {
          if (requestId.current !== currentRequestId) return;
          if (data) setTotalItems(data.totalElements);
          setItems(data.results);
        })
        .catch(catchCancel)
        .catch(parseServerError(dispatch, { showTryAgain: true }))
        .finally(() => {
          if (requestId.current !== currentRequestId) return;
          setIsSearch(Boolean(searchTerm));
          setLoadedSearchTerm(searchTerm || '');
          setLoaded(true);
        });
    };
    setLoaded(false);
    setReload(false);
    if (loadingOrg) {
      return () => {
        requestId.current += 1;
      };
    }
    if (debouncedSearchTerm) {
      loadItems(sort, debouncedSearchTerm);
    } else {
      loadItems(sort);
    }
    return () => {
      requestId.current += 1;
      cancelRequest?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    debouncedSearchTerm,
    sort,
    sortByOverride,
    apiResource,
    searchField,
    cropSeason,
    productType,
    taskType,
    label,
    dispatch,
    organization?.id,
    loadingOrg,
    pageNo,
    reload
    // searchText,
    // _sortFields
  ]);

  return {
    view,
    setView,
    sort,
    setSort,
    setSortByOverride,
    isSearch,
    setSearchText,
    setCropSeason,
    setLabel,
    setTaskType,
    productType,
    setProductType,
    loaded,
    isLoading: isLoading || !loaded,
    items,
    setItems,
    setReload,
    totalItems
  };
};

export default useLandingPageData;
